import { useEffect, useState } from "react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Copy, Download, ExternalLink, Printer, Check, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const ShareLinkDialog = ({ open, onOpenChange, link }) => {
  const [qr, setQr] = useState("");
  const [copied, setCopied] = useState(false);
  const [rotated, setRotated] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRotated(null); }, [link]);
  const url = rotated || link?.url || "";
  const rotate = async () => {
    if (!window.confirm("سيتوقف الرابط القديم عن العمل نهائياً، وتحتاج ترسل الرابط الجديد. متأكد؟")) return;
    setBusy(true);
    try { setRotated(await link.onRotate()); toast.success("تم تغيير الرابط، والرابط القديم توقف"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تغيير الرابط"); }
    finally { setBusy(false); }
  };
  const title = link?.title || "";

  useEffect(() => {
    if (open && url) {
      QRCode.toDataURL(url, { width: 320, margin: 2, color: { dark: "#0f172a", light: "#ffffff" } })
        .then(setQr).catch(() => setQr(""));
    } else {
      setQr("");
    }
  }, [open, url]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("تم نسخ الرابط");
    } catch { toast.error("تعذّر النسخ"); }
  };

  const download = () => {
    if (!qr) return;
    const a = document.createElement("a");
    a.href = qr;
    a.download = `${title || "qr"}.png`;
    a.click();
  };

  const printQr = () => {
    if (!qr) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html dir="rtl"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><title>${title}</title></head>
      <body style="font-family:SaudiRiyal,sans-serif;text-align:center;padding:40px">
      <h2>${title}</h2>
      <img src="${qr}" style="width:320px;height:320px"/>
      <p style="direction:ltr;color:#475569;word-break:break-all">${url}</p>
      <script>window.onload=function(){window.print()}<\/script>
      </body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="font-body sm:max-w-md" dir="rtl" data-testid="share-link-dialog">
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-lg">{title}</DialogTitle>
          <DialogDescription className="text-start">
            انسخ الرابط أو شاركه، أو اطبع رمز QR ليمسحه الموظفون بالجوال.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center py-2">
          {qr ? (
            <img src={qr} alt="QR" data-testid="share-link-qr" className="w-56 h-56 rounded-xl border border-slate-200 p-2 bg-white" />
          ) : (
            <div className="w-56 h-56 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-slate-300 text-sm">جارٍ التوليد…</div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Input value={url} readOnly dir="ltr" data-testid="share-link-url" className="bg-slate-50 text-xs" onFocus={(e) => e.target.select()} />
          <Button onClick={copy} data-testid="share-link-copy" className="gap-1.5 shrink-0 bg-blue-600 hover:bg-blue-700">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "تم" : "نسخ"}
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2 pt-1">
          <Button variant="outline" onClick={() => window.open(url, "_blank", "noopener")} data-testid="share-link-open" className="gap-1.5">
            <ExternalLink className="h-4 w-4" /> فتح
          </Button>
          <Button variant="outline" onClick={download} data-testid="share-link-download" className="gap-1.5" disabled={!qr}>
            <Download className="h-4 w-4" /> حفظ QR
          </Button>
          <Button variant="outline" onClick={printQr} data-testid="share-link-print" className="gap-1.5" disabled={!qr}>
            <Printer className="h-4 w-4" /> طباعة
          </Button>
        </div>
        {link?.onRotate && (
          <Button variant="outline" onClick={rotate} disabled={busy} data-testid="share-link-rotate" className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> تغيير الرابط (إيقاف الرابط القديم)
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
